import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { useDemoStore } from '@/store/demoStore';
import { web3Service } from '@/services/web3';
import { 
  Image, 
  Upload, 
  AlertCircle,
  Info,
  DollarSign,
  Tag,
  Plus,
  Trash2,
  Video
} from 'lucide-react';
import useApiService from '@/services/apiService';

interface Drop {
  id: string;
  title: string;
  creatorAddress: string;
}

interface Attribute {
  trait_type: string;
  value: string;
}

export default function MintNFTPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { isDemoMode } = useDemoStore();
  const apiService = useApiService();
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [isVideo, setIsVideo] = useState(false);
  const [selectedDrop, setSelectedDrop] = useState<string>('');
  const [drops, setDrops] = useState<Drop[]>([]);
  const [royaltyPercentage, setRoyaltyPercentage] = useState(10);
  const [price, setPrice] = useState('');
  const [listForSale, setListForSale] = useState(false);
  const [attributes, setAttributes] = useState<Attribute[]>([
    { trait_type: '', value: '' }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [nftId, setNftId] = useState('');
  const [step, setStep] = useState(1);
  const [isLoadingDrops, setIsLoadingDrops] = useState(false);

  // Fetch user's drops
  useEffect(() => {
    const fetchDrops = async () => {
      if (!user) return;
      
      setIsLoadingDrops(true);
      try {
        if (isDemoMode) {
          // In demo mode, use mock data
          const demoDrops = [
            { id: 'demo-drop-1', title: 'Digital Art Collection 2025', creatorAddress: user.walletAddress },
            { id: 'demo-drop-2', title: 'Metaverse Landscapes', creatorAddress: user.walletAddress }
          ];
          setDrops(demoDrops);
        } else {
          // In real mode, fetch from API
          const userDrops = await apiService.getUserDrops(user.id);
          setDrops(userDrops);
        }
      } catch (error) {
        console.error('Error fetching drops:', error);
        toast.error('Failed to load your drops');
      } finally {
        setIsLoadingDrops(false);
      }
    };
    
    fetchDrops();
  }, [user, isDemoMode, apiService]);

  // Handle media file selection
  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMediaFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      
      // Check if it's a video
      setIsVideo(file.type.startsWith('video/'));
    }
  };

  // Add attribute
  const addAttribute = () => {
    setAttributes([...attributes, { trait_type: '', value: '' }]);
  };

  // Remove attribute
  const removeAttribute = (index: number) => {
    const newAttributes = [...attributes];
    newAttributes.splice(index, 1);
    setAttributes(newAttributes);
  };

  // Update attribute
  const updateAttribute = (index: number, field: 'trait_type' | 'value', value: string) => {
    const newAttributes = [...attributes];
    newAttributes[index][field] = value;
    setAttributes(newAttributes);
  };

  // Mint NFT
  const handleMintNFT = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Please enter a name for your NFT');
      return;
    }
    
    if (!mediaFile && !mediaPreview) {
      setError('Please upload an image or video for your NFT');
      return;
    }
    
    if (listForSale && (!price || parseFloat(price) <= 0)) {
      setError('Please enter a valid price');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      if (isDemoMode) {
        // In demo mode, simulate NFT minting
        setTimeout(() => {
          toast.success('NFT minted successfully!');
          setNftId('demo-nft-' + Math.floor(Math.random() * 1000));
          setStep(2);
          setIsLoading(false);
        }, 1500);
      } else {
        // In real mode, mint NFT via API and blockchain
        // 1. Upload media to IPFS
        const mediaUri = await uploadMediaToIPFS();
        console.log("Media URI:", mediaUri); // Using mediaUri
        
        // 2. Upload metadata to IPFS with the media URI
        const metadataUri = await uploadMetadataToIPFS(mediaUri);
        
        // 3. Mint NFT on blockchain
        const tokenId = await web3Service.mintNFT(
          user?.walletAddress || '',
          metadataUri,
          user?.walletAddress || '',
          royaltyPercentage
        );
        
        // 4. Save NFT details to backend
        const nftData = {
          name,
          description,
          image: mediaUri,
          dropId: selectedDrop || null,
          tokenId,
          contractAddress: web3Service.getNFTContractAddress(),
          royaltyPercentage,
          attributes: attributes.filter(attr => attr.trait_type && attr.value),
          isVideo,
        };
        
        const nft = await apiService.createNFT(nftData);
        
        // 5. List for sale if selected
        if (listForSale && price) {
          await web3Service.listNFTForSale(
            web3Service.getNFTContractAddress(),
            tokenId,
            price,
            true // isERC721
          );
          
          // Update NFT with listing info
          await apiService.updateNFT(nft.id, { 
            forSale: true,
            price
          });
        }
        
        toast.success('NFT minted successfully!');
        setNftId(nft.id);
        setStep(2);
      }
    } catch (error) {
      console.error('Error minting NFT:', error);
      toast.error('Failed to mint NFT');
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Upload media to IPFS (mock implementation)
  const uploadMediaToIPFS = async () => {
    // In a real implementation, this would upload to IPFS
    // For now, we'll just return a mock URI
    return 'ipfs://QmAbC123456789abcdef123456789abcdef123456/image.png';
  };

  // Upload metadata to IPFS (mock implementation)
  const uploadMetadataToIPFS = async (_mediaUri: string) => {
    // In a real implementation, this would upload to IPFS
    // For now, we'll just return a mock URI
    return 'ipfs://QmXyZ123456789abcdef123456789abcdef123456/metadata.json';
  };

  // Go to NFTs dashboard
  const handleGoToDashboard = () => {
    navigate('/dashboard/nfts');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center mb-8">
        <Image className="h-6 w-6 mr-2 text-primary" />
        <h1 className="text-3xl font-bold text-white">Mint New NFT</h1>
      </div>
      
      {step === 1 ? (
        <div className="bg-dark-800 rounded-xl p-6 shadow-md">
          <form onSubmit={handleMintNFT}>
            {error && (
              <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-red-200">{error}</p>
              </div>
            )}
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">NFT Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                placeholder="Enter a name for your NFT"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none min-h-[120px]"
                placeholder="Describe your NFT (optional)"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Upload Media *</label>
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <div className="relative border-2 border-dashed border-dark-600 rounded-lg p-4 hover:border-primary transition-colors">
                    <input
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleMediaChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center py-4">
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-400">Click or drag to upload image or video</p>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF, MP4 (max 10MB)</p>
                    </div>
                  </div>
                </div>
                
                {mediaPreview && (
                  <div className="w-1/3">
                    <div className="aspect-square rounded-lg overflow-hidden bg-dark-700">
                      {isVideo ? (
                        <div className="relative w-full h-full">
                          <video 
                            src={mediaPreview} 
                            className="w-full h-full object-cover"
                            controls
                          />
                          <div className="absolute top-2 right-2 bg-dark-900/80 text-white text-xs px-2 py-1 rounded-full">
                            <Video className="h-3 w-3 inline mr-1" />
                            Video
                          </div>
                        </div>
                      ) : (
                        <img 
                          src={mediaPreview} 
                          alt="Media preview" 
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Collection (Optional)</label>
              <select
                value={selectedDrop}
                onChange={(e) => setSelectedDrop(e.target.value)}
                className="w-full bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
              >
                <option value="">No Collection</option>
                {isLoadingDrops ? (
                  <option disabled>Loading collections...</option>
                ) : (
                  drops.map((drop) => (
                    <option key={drop.id} value={drop.id}>
                      {drop.title}
                    </option>
                  ))
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">Add this NFT to one of your collections</p>
            </div>
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Royalty Percentage</label>
              <input
                type="range"
                min="0"
                max="20"
                step="1"
                value={royaltyPercentage}
                onChange={(e) => setRoyaltyPercentage(parseInt(e.target.value))}
                className="w-full h-2 bg-dark-600 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between mt-2">
                <span className="text-sm text-gray-400">0%</span>
                <span className="text-sm text-primary font-medium">{royaltyPercentage}%</span>
                <span className="text-sm text-gray-400">20%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Percentage of secondary sales you'll receive as royalties</p>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="list-for-sale"
                  checked={listForSale}
                  onChange={(e) => setListForSale(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-600 text-primary focus:ring-primary"
                />
                <label htmlFor="list-for-sale" className="ml-2 text-white font-medium flex items-center">
                  <Tag className="h-4 w-4 mr-1 text-primary" />
                  List for Sale Immediately
                </label>
              </div>
              
              {listForSale && (
                <div className="bg-dark-700 p-4 rounded-lg border border-dark-600 ml-6">
                  <div className="mb-4">
                    <label className="block text-gray-400 text-sm mb-2">Price (in dUSD)</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        className="w-full bg-dark-900 text-white rounded-lg p-3 pl-10 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-white font-medium">Attributes</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAttribute}
                  className="text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Attribute
                </Button>
              </div>
              
              <div className="space-y-3">
                {attributes.map((attr, index) => (
                  <div key={index} className="flex space-x-3">
                    <input
                      type="text"
                      value={attr.trait_type}
                      onChange={(e) => updateAttribute(index, 'trait_type', e.target.value)}
                      className="flex-1 bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                      placeholder="Trait name (e.g. Color)"
                    />
                    <input
                      type="text"
                      value={attr.value}
                      onChange={(e) => updateAttribute(index, 'value', e.target.value)}
                      className="flex-1 bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                      placeholder="Value (e.g. Blue)"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAttribute(index)}
                      className="text-red-500 hover:text-red-400 hover:bg-red-900/20"
                      disabled={attributes.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">Add traits to your NFT to increase its uniqueness</p>
            </div>
            
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                className="mr-4"
                onClick={() => navigate('/dashboard/nfts')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-primary hover:bg-primary/90"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    <span>Minting...</span>
                  </div>
                ) : (
                  <span>Mint NFT</span>
                )}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-dark-800 rounded-xl p-6 shadow-md">
          <div className="mb-6 p-4 bg-green-900/30 border border-green-700 rounded-lg flex items-start">
            <Info className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-green-200 font-medium">NFT minted successfully!</p>
              <p className="text-green-300 text-sm mt-1">Your NFT has been minted and is now part of your collection.</p>
            </div>
          </div>
          
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white mb-4">NFT Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">NFT Name</label>
                <p className="text-white font-medium">{name}</p>
              </div>
              
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">Collection</label>
                <p className="text-white font-medium">
                  {selectedDrop ? drops.find(d => d.id === selectedDrop)?.title || 'Selected Collection' : 'No Collection'}
                </p>
              </div>
              
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">Royalty</label>
                <p className="text-white font-medium">{royaltyPercentage}%</p>
              </div>
              
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">Status</label>
                <p className="text-white font-medium flex items-center">
                  {listForSale ? (
                    <>
                      <Tag className="h-4 w-4 mr-1 text-primary" />
                      Listed for {price} dUSD
                    </>
                  ) : (
                    'Not Listed'
                  )}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">NFT ID</h2>
            
            <div className="bg-dark-700 p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-gray-400 text-sm">Token ID</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(nftId);
                    toast.success('NFT ID copied to clipboard');
                  }}
                  className="text-xs"
                >
                  Copy
                </Button>
              </div>
              <div className="bg-dark-900 p-3 rounded border border-dark-600 font-mono text-primary">
                {nftId}
              </div>
            </div>
          </div>
          
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setStep(1)}
            >
              Mint Another NFT
            </Button>
            <Button
              onClick={handleGoToDashboard}
              className="bg-primary hover:bg-primary/90"
            >
              Go to NFTs Dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}